import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  BookOpen, 
  ChevronRight,
  ChevronLeft,
  Home,
  CreditCard,
  PieChart,
  Receipt,
  Calendar,
  TrendingUp,
  Target,
  Wallet,
  LineChart,
  GraduationCap,
  Users,
  Settings,
  Shield,
  HelpCircle,
  ArrowLeft,
  Menu,
  X,
  Search,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Info,
  Lightbulb
} from "lucide-react";

// Documentation sections configuration
const docSections = [
  { id: "getting-started", title: "Getting Started", icon: Home },
  { id: "dashboard", title: "Dashboard Overview", icon: PieChart },
  { id: "bank-connection", title: "Connecting Your Bank", icon: CreditCard },
  { id: "transactions", title: "Transactions", icon: CreditCard },
  { id: "budget", title: "Budget Management", icon: PieChart },
  { id: "bills", title: "Bills & Payment Tracking", icon: Receipt, isNew: true },
  { id: "calendar", title: "Calendar Integration", icon: Calendar },
  { id: "monthly-plan", title: "Monthly Plan", icon: Calendar },
  { id: "insights", title: "Insights & AI Predictions", icon: TrendingUp, isNew: true },
  { id: "goals", title: "Financial Goals", icon: Target },
  { id: "net-worth", title: "Net Worth Tracking", icon: Wallet },
  { id: "cash-flow", title: "Cash Flow Forecasting", icon: LineChart },
  { id: "debt", title: "Debt Payoff Planning", icon: CreditCard },
  { id: "courses", title: "Financial Courses", icon: GraduationCap },
  { id: "household", title: "Household Sharing", icon: Users, isNew: true },
  { id: "settings", title: "Settings & Account", icon: Settings },
  { id: "pricing", title: "Subscription & Pricing", icon: CreditCard },
  { id: "privacy", title: "Privacy & Security", icon: Shield },
  { id: "faq", title: "FAQ & Troubleshooting", icon: HelpCircle },
];

// Tip component
const Tip = ({ children }) => (
  <div className="flex gap-3 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 my-4">
    <Lightbulb className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
    <div className="text-sm text-emerald-800 dark:text-emerald-200">{children}</div>
  </div>
);

// Warning component
const Warning = ({ children }) => (
  <div className="flex gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 my-4">
    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
    <div className="text-sm text-amber-800 dark:text-amber-200">{children}</div>
  </div>
);

// Info component
const InfoBox = ({ children }) => (
  <div className="flex gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 my-4">
    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
    <div className="text-sm text-blue-800 dark:text-blue-200">{children}</div>
  </div>
);

// Step component
const Step = ({ number, title, children }) => (
  <div className="flex gap-4 my-4">
    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-semibold mb-1">{title}</h4>
      <div className="text-muted-foreground">{children}</div>
    </div>
  </div>
);

// Feature list component
const FeatureList = ({ items }) => (
  <ul className="space-y-2 my-4">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// Documentation content for each section
const docContent = {
  "getting-started": {
    title: "Getting Started",
    description: "Create your account and begin your journey to financial clarity",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Welcome to Tally</h2>
          <p className="text-muted-foreground mb-4">
            Tally is Canada's simple budgeting app designed to help you take control of your finances. 
            Whether you're a student learning to budget, a professional managing household expenses, 
            or a senior tracking retirement income, Tally has you covered.
          </p>
          <FeatureList items={[
            "Connect your Canadian bank accounts securely",
            "Track spending across all accounts in one place",
            "Set and monitor budgets by category",
            "Never miss a bill payment",
            "Get AI-powered insights and predictions",
            "Share finances with household members"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Creating Your Account</h2>
          <Step number="1" title="Visit Tally">
            Go to <a href="https://usetally.ca" className="text-primary hover:underline">usetally.ca</a> and click <strong>Get Started</strong> or <strong>Start Free Trial</strong>.
          </Step>
          <Step number="2" title="Enter Your Information">
            Provide your email address, create a secure password (minimum 8 characters), and enter your full name.
          </Step>
          <Step number="3" title="Enter Your Birthdate">
            Your birthdate determines your plan eligibility:
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Under 25:</strong> Tally Student (Free)</li>
              <li><strong>25-64:</strong> Tally Power ($14.99/month)</li>
              <li><strong>65+:</strong> Tally Senior (Free)</li>
            </ul>
          </Step>
          <Step number="4" title="Start Your Trial (Power Users)">
            If you're a Power user, you'll start with a 7-day free trial. Enter payment details to begin—you won't be charged until the trial ends.
          </Step>
          <Tip>
            You can cancel your trial anytime before it ends from Settings → Subscription. No charge if you cancel during the trial period.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Your First Login</h2>
          <p className="text-muted-foreground mb-4">
            After creating your account, you'll be guided through the onboarding process:
          </p>
          <Step number="1" title="Connect Your Bank">
            Link your Canadian bank accounts through Plaid. This is secure, read-only access—Tally can never move your money.
          </Step>
          <Step number="2" title="Review Your Accounts">
            See all connected checking, savings, and credit accounts. Verify the accounts you want to track.
          </Step>
          <Step number="3" title="Explore the Dashboard">
            Your financial command center is ready! View balances, recent transactions, and upcoming bills at a glance.
          </Step>
          <InfoBox>
            Don't want to connect a bank right away? You can skip this step and add accounts later from Settings. You can still manually add transactions and bills.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">System Requirements</h2>
          <p className="text-muted-foreground mb-4">
            Tally works in any modern web browser:
          </p>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Chrome (recommended)</li>
            <li>Firefox</li>
            <li>Safari</li>
            <li>Edge</li>
            <li>Mobile browsers (iOS Safari, Chrome for Android)</li>
          </ul>
          <Tip>
            For the best experience on mobile, add Tally to your home screen. On iOS, tap Share → Add to Home Screen. On Android, tap the menu → Add to Home Screen.
          </Tip>
        </section>
      </>
    )
  },

  "dashboard": {
    title: "Dashboard Overview",
    description: "Your financial command center at a glance",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Understanding Your Dashboard</h2>
          <p className="text-muted-foreground mb-4">
            The Dashboard is your financial headquarters. It gives you a complete snapshot of your money situation 
            so you can make informed decisions quickly.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Key Metrics</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-emerald-600">Cash Balance</h3>
                <p className="text-sm text-muted-foreground">Total money across all checking and savings accounts</p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-red-600">Credit Balance</h3>
                <p className="text-sm text-muted-foreground">Outstanding balance on all credit cards</p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-blue-600">Monthly Spending</h3>
                <p className="text-sm text-muted-foreground">Total spent across all categories this month</p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-purple-600">Savings Rate</h3>
                <p className="text-sm text-muted-foreground">Percentage of income you've saved this month</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Dashboard Cards</h2>
          
          <h3 className="text-lg font-semibold mt-6 mb-2">AI Summary</h3>
          <p className="text-muted-foreground mb-4">
            Powered by GPT-4o, this card provides personalized insights about your financial situation. 
            It analyzes your spending patterns, upcoming bills, and account balances to give you actionable advice.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Recent Transactions</h3>
          <p className="text-muted-foreground mb-4">
            Quick view of your last 5 transactions across all connected accounts. Click any transaction 
            to see details or change its category.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Upcoming Bills</h3>
          <p className="text-muted-foreground mb-4">
            Bills due in the next 7 days. Shows merchant name, amount, and days until due. 
            Bills due within 3 days are highlighted in amber.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Budget Progress</h3>
          <p className="text-muted-foreground mb-4">
            Visual progress bars showing spending vs. budget for your top categories. 
            Green means on track, yellow means approaching limit, red means over budget.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Cash Flow Alert</h3>
          <p className="text-muted-foreground mb-4">
            If our 90-day forecast detects a potential shortfall (balance going negative), 
            you'll see a warning here with the date and amount needed.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Quick Actions</h2>
          <FeatureList items={[
            "Sync Now — Manually refresh data from your bank",
            "Add Transaction — Enter a cash purchase or manual transaction",
            "View All — Navigate to detailed views for any section"
          ]} />
          <Tip>
            Your data syncs automatically based on your plan (daily for Power, weekly for Student/Senior). 
            Use Sync Now if you need the latest data immediately.
          </Tip>
        </section>
      </>
    )
  },

  "bank-connection": {
    title: "Connecting Your Bank",
    description: "Securely link your Canadian bank accounts",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">About Bank Connections</h2>
          <p className="text-muted-foreground mb-4">
            Tally uses <strong>Plaid</strong> to securely connect to your bank. Plaid is a trusted financial 
            technology company used by thousands of apps and banks worldwide, including major Canadian institutions.
          </p>
          <InfoBox>
            <strong>Your security is our priority.</strong> Tally has read-only access to your accounts. 
            We can see transactions and balances, but we can never move money, make payments, or modify your accounts.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Supported Canadian Banks</h2>
          <div className="grid md:grid-cols-3 gap-2">
            {[
              "TD Canada Trust", "RBC Royal Bank", "Scotiabank", "BMO", "CIBC",
              "National Bank", "Tangerine", "Simplii Financial", "Desjardins",
              "ATB Financial", "Coast Capital", "Meridian", "Vancity",
              "First Ontario", "HSBC Canada", "Manulife Bank"
            ].map((bank) => (
              <div key={bank} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-sm">{bank}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            And 50+ more Canadian institutions. If your bank isn't listed, try searching during the connection process.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">How to Connect Your Bank</h2>
          <Step number="1" title="Navigate to Settings">
            Go to Settings → Connected Accounts and click <strong>Connect New Bank</strong>.
          </Step>
          <Step number="2" title="Search for Your Bank">
            Type your bank's name in the search box. Select it from the list.
          </Step>
          <Step number="3" title="Enter Credentials">
            Enter your online banking username and password. This is done securely through Plaid—Tally never sees your password.
          </Step>
          <Step number="4" title="Complete Verification">
            Some banks require additional verification (security questions, SMS code, or app approval). Complete these steps as prompted.
          </Step>
          <Step number="5" title="Select Accounts">
            Choose which accounts to connect. You can select checking, savings, and credit card accounts.
          </Step>
          <Step number="6" title="Wait for Initial Sync">
            The first sync takes 1-2 minutes. You'll see your accounts and transactions once complete.
          </Step>
          <Warning>
            Some banks (especially credit unions) may require you to enable third-party access in your online banking settings first.
          </Warning>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Sync Frequency</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Plan</th>
                  <th className="text-left py-2">Automatic Sync</th>
                  <th className="text-left py-2">Manual Sync</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 font-medium">Tally Power</td>
                  <td className="py-2">Daily</td>
                  <td className="py-2">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Tally Student</td>
                  <td className="py-2">Weekly (Wednesdays)</td>
                  <td className="py-2">Once per week</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">Tally Senior</td>
                  <td className="py-2">Weekly (Wednesdays)</td>
                  <td className="py-2">Once per week</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Disconnecting a Bank</h2>
          <p className="text-muted-foreground mb-4">
            To remove a bank connection:
          </p>
          <Step number="1" title="Go to Settings">
            Navigate to Settings → Connected Accounts.
          </Step>
          <Step number="2" title="Find the Account">
            Locate the bank you want to disconnect.
          </Step>
          <Step number="3" title="Click Disconnect">
            Click the disconnect button and confirm. Historical transactions will be preserved.
          </Step>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Troubleshooting</h2>
          <h3 className="font-semibold mt-4 mb-2">Bank won't connect?</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Verify your online banking credentials are correct</li>
            <li>Check if your bank requires enabling third-party access</li>
            <li>Try using your bank's website (not app) credentials</li>
            <li>Some banks have connection delays—try again in a few hours</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-2">Connection keeps breaking?</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Your bank may require periodic re-authentication</li>
            <li>Go to Settings → Connected Accounts and click "Reconnect"</li>
            <li>Some banks reset connections after password changes</li>
          </ul>
        </section>
      </>
    )
  },

  "transactions": {
    title: "Transactions",
    description: "View, search, filter, and categorize all your transactions",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Transaction Overview</h2>
          <p className="text-muted-foreground mb-4">
            The Transactions page shows all your financial activity across connected accounts. 
            View spending patterns, search for specific purchases, and ensure everything is categorized correctly.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Viewing Transactions</h2>
          <p className="text-muted-foreground mb-4">Each transaction displays:</p>
          <FeatureList items={[
            "Date of the transaction",
            "Merchant name",
            "Amount (negative for expenses, positive for income)",
            "Category (automatically assigned or manually set)",
            "Account it came from",
            "Pending status (if not yet cleared)"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Searching & Filtering</h2>
          <h3 className="font-semibold mt-4 mb-2">Search</h3>
          <p className="text-muted-foreground mb-4">
            Use the search bar to find transactions by merchant name. For example, search "Tim Hortons" 
            to see all your coffee purchases.
          </p>

          <h3 className="font-semibold mt-4 mb-2">Filter by Account</h3>
          <p className="text-muted-foreground mb-4">
            Select a specific account to view only its transactions. Useful for reconciling individual accounts.
          </p>

          <h3 className="font-semibold mt-4 mb-2">Filter by Category</h3>
          <p className="text-muted-foreground mb-4">
            Choose a category to see all related spending. Great for analyzing specific expense types.
          </p>

          <h3 className="font-semibold mt-4 mb-2">Date Range</h3>
          <p className="text-muted-foreground mb-4">
            Set custom start and end dates to view transactions from a specific period.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Transaction Categories</h2>
          <p className="text-muted-foreground mb-4">
            Tally automatically categorizes transactions using machine learning. Categories include:
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            {[
              { cat: "Groceries", ex: "Loblaws, Metro, No Frills" },
              { cat: "Dining", ex: "Restaurants, Tim Hortons, Skip" },
              { cat: "Transportation", ex: "Gas, Uber, TTC, Presto" },
              { cat: "Shopping", ex: "Amazon, Walmart, Canadian Tire" },
              { cat: "Entertainment", ex: "Netflix, Spotify, Movies" },
              { cat: "Utilities", ex: "Hydro, Gas, Internet" },
              { cat: "Healthcare", ex: "Pharmacies, Clinics" },
              { cat: "Income", ex: "Salary, E-Transfers received" }
            ].map(({ cat, ex }) => (
              <div key={cat} className="p-3 rounded bg-muted/50">
                <span className="font-medium">{cat}</span>
                <p className="text-xs text-muted-foreground">{ex}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Changing Categories</h2>
          <Step number="1" title="Click the Transaction">
            Select any transaction to open its details.
          </Step>
          <Step number="2" title="Select New Category">
            Choose the correct category from the dropdown menu.
          </Step>
          <Step number="3" title="Auto-Save">
            Changes save automatically. The new category will be used for budget tracking.
          </Step>
          <Tip>
            Re-categorizing transactions helps Tally learn your preferences. Over time, similar transactions 
            may be automatically assigned to your preferred category.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Adding Manual Transactions</h2>
          <p className="text-muted-foreground mb-4">
            For cash purchases or accounts not connected to Tally:
          </p>
          <Step number="1" title="Click Add Transaction">
            Use the + button or "Add Transaction" button.
          </Step>
          <Step number="2" title="Enter Details">
            Fill in the date, amount (use negative for expenses), merchant name, and category.
          </Step>
          <Step number="3" title="Select Account">
            Choose the relevant account or select "Cash" for cash transactions.
          </Step>
          <Step number="4" title="Save">
            Click Save. Manual transactions appear with a special indicator.
          </Step>
          <InfoBox>
            Manual transactions can be edited or deleted. Imported transactions from your bank cannot be deleted.
          </InfoBox>
        </section>
      </>
    )
  },

  "budget": {
    title: "Budget Management",
    description: "Create spending limits and track your progress",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Why Budget?</h2>
          <p className="text-muted-foreground mb-4">
            Budgeting is the foundation of financial health. It helps you:
          </p>
          <FeatureList items={[
            "Understand where your money goes",
            "Identify areas to cut back",
            "Ensure bills are covered before discretionary spending",
            "Build savings consistently",
            "Reduce financial stress"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Setting Up Budgets</h2>
          <Step number="1" title="Navigate to Budget">
            Go to the Budget page from the sidebar.
          </Step>
          <Step number="2" title="Click Edit Budgets">
            Click the pencil icon or "Edit Budgets" button.
          </Step>
          <Step number="3" title="Set Category Limits">
            Enter monthly spending limits for each category based on your income and priorities.
          </Step>
          <Step number="4" title="Save">
            Click Save. Your budgets are now active for the current month.
          </Step>
          <Tip>
            Start with last month's actual spending as a baseline. Then adjust categories where you want to spend less.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Budget Categories</h2>
          <p className="text-muted-foreground mb-4">Set budgets for:</p>
          <div className="grid md:grid-cols-3 gap-2">
            {["Groceries", "Dining", "Transportation", "Shopping", "Entertainment", "Utilities", "Healthcare", "Personal Care", "Other"].map((cat) => (
              <div key={cat} className="p-2 rounded bg-muted/50 text-sm font-medium">{cat}</div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Tracking Progress</h2>
          <p className="text-muted-foreground mb-4">Each category shows:</p>
          <FeatureList items={[
            "Spent — Amount spent this month in the category",
            "Budget — Your set limit",
            "Progress Bar — Visual indicator of spending vs. budget",
            "Remaining — How much you can still spend"
          ]} />
          
          <h3 className="font-semibold mt-6 mb-2">Color Indicators</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-emerald-500" />
              <span><strong>Green:</strong> Under 70% of budget — You're on track</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-amber-500" />
              <span><strong>Yellow:</strong> 70-100% of budget — Approaching limit</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-red-500" />
              <span><strong>Red:</strong> Over 100% — Budget exceeded</span>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Budget Tips</h2>
          <Tip>
            <strong>50/30/20 Rule:</strong> Aim for 50% needs (housing, utilities, groceries), 
            30% wants (dining, entertainment), and 20% savings/debt repayment.
          </Tip>
          <Tip>
            <strong>Review Weekly:</strong> Check your budget progress every Sunday. 
            This gives you time to adjust spending before month-end.
          </Tip>
          <Tip>
            <strong>Be Realistic:</strong> Budgets that are too strict often fail. 
            Include some fun money to stay motivated.
          </Tip>
        </section>
      </>
    )
  },

  "bills": {
    title: "Bills & Payment Tracking",
    description: "Manage recurring bills and track payment status",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Bill Management Overview</h2>
          <p className="text-muted-foreground mb-4">
            Never miss a bill payment again. Tally automatically detects your recurring bills and helps you 
            track which ones you've paid each month.
          </p>
          <Badge className="bg-emerald-100 text-emerald-700">New Feature</Badge>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Automatic Bill Detection</h2>
          <p className="text-muted-foreground mb-4">
            Tally analyzes your transaction history to find recurring payments:
          </p>
          <FeatureList items={[
            "Subscriptions (Netflix, Spotify, Disney+)",
            "Utilities (Hydro, Gas, Internet, Phone)",
            "Insurance (Car, Home, Life)",
            "Loan Payments (Car loan, Student loan)",
            "Rent/Mortgage (detected from regular large payments)",
            "Gym memberships and other recurring services"
          ]} />
          <InfoBox>
            Bills are detected after 2+ occurrences of similar transactions. New accounts may take 1-2 months to show all recurring bills.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Payment Tracking</h2>
          <p className="text-muted-foreground mb-4">
            Track which bills you've paid each month with our new payment tracking feature.
          </p>
          
          <h3 className="font-semibold mt-6 mb-2">Marking Bills as Paid</h3>
          <Step number="1" title="Find the Bill">
            Go to the Bills page and locate the bill you've paid.
          </Step>
          <Step number="2" title="Click the Circle">
            Click the circle icon to the left of the bill name.
          </Step>
          <Step number="3" title="Confirm Payment">
            The bill is marked as paid, and a corresponding transaction is automatically created.
          </Step>
          
          <h3 className="font-semibold mt-6 mb-2">Payment Progress</h3>
          <p className="text-muted-foreground mb-4">
            At the top of the Bills page, you'll see:
          </p>
          <FeatureList items={[
            "Total paid vs. remaining this month",
            "Number of bills paid vs. unpaid",
            "Visual progress bar showing completion",
            "Paid bills show a checkmark and strikethrough"
          ]} />
          <Tip>
            Marking bills as paid helps you stay organized and creates an accurate record of your expenses.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Adding Manual Bills</h2>
          <p className="text-muted-foreground mb-4">
            For bills not automatically detected:
          </p>
          <Step number="1" title="Click Add Bill">
            Use the + button on the Bills page.
          </Step>
          <Step number="2" title="Enter Details">
            Provide the bill name, average amount, next due date, and frequency (weekly, bi-weekly, monthly, or yearly).
          </Step>
          <Step number="3" title="Save">
            Click Save. The bill will appear in your list and can be synced to your calendar.
          </Step>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Editing Bills</h2>
          <p className="text-muted-foreground mb-4">Click on any bill to:</p>
          <FeatureList items={[
            "Change the display name (e.g., 'Rogers' to 'Phone Bill')",
            "Toggle calendar sync on/off",
            "Enable/disable the bill (hidden from view but not deleted)",
            "Delete manual bills"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Bill Alerts</h2>
          <p className="text-muted-foreground mb-4">
            Bills due within 3 days are highlighted in amber on your dashboard and bills page, 
            so you never miss a payment.
          </p>
          <Warning>
            Late bill payments can affect your credit score. Use Tally's calendar sync to get reminders before due dates.
          </Warning>
        </section>
      </>
    )
  },

  "calendar": {
    title: "Calendar Integration",
    description: "Sync bills to Apple, Google, or Outlook Calendar",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Calendar Sync Overview</h2>
          <p className="text-muted-foreground mb-4">
            Get bill reminders in your favorite calendar app. Tally creates an ICS feed that you can 
            subscribe to from any calendar application.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Supported Calendars</h2>
          <FeatureList items={[
            "Apple Calendar (iPhone, iPad, Mac)",
            "Google Calendar",
            "Microsoft Outlook",
            "Any calendar app that supports ICS subscriptions"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Setup Instructions</h2>
          
          <h3 className="font-semibold mt-6 mb-3">Step 1: Generate Your Calendar Link</h3>
          <Step number="1" title="Go to Settings">
            Navigate to Settings → Calendar Settings.
          </Step>
          <Step number="2" title="Generate Link">
            Click "Generate Calendar Link" to create your unique ICS feed URL.
          </Step>
          <Step number="3" title="Copy URL">
            Copy the URL to your clipboard.
          </Step>

          <h3 className="font-semibold mt-8 mb-3">Apple Calendar (iPhone/Mac)</h3>
          <Step number="1" title="Open Settings">
            Go to Settings → Calendar → Accounts (on iPhone) or Calendar → Accounts (on Mac).
          </Step>
          <Step number="2" title="Add Account">
            Tap "Add Account" → "Other" → "Add Subscribed Calendar".
          </Step>
          <Step number="3" title="Paste URL">
            Paste your Tally calendar URL and tap Subscribe.
          </Step>

          <h3 className="font-semibold mt-8 mb-3">Google Calendar</h3>
          <Step number="1" title="Open Google Calendar">
            Go to calendar.google.com in a web browser.
          </Step>
          <Step number="2" title="Add Calendar">
            Click the + next to "Other calendars" → "From URL".
          </Step>
          <Step number="3" title="Paste URL">
            Paste your Tally calendar URL and click "Add calendar".
          </Step>

          <h3 className="font-semibold mt-8 mb-3">Microsoft Outlook</h3>
          <Step number="1" title="Open Outlook Calendar">
            Go to outlook.com or open the Outlook app.
          </Step>
          <Step number="2" title="Add Calendar">
            Click "Add calendar" → "Subscribe from web".
          </Step>
          <Step number="3" title="Paste URL">
            Paste your Tally calendar URL and click "Import".
          </Step>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">What Gets Synced</h2>
          <p className="text-muted-foreground mb-4">
            Each bill with calendar sync enabled creates events showing:
          </p>
          <FeatureList items={[
            "Bill name",
            "Due date",
            "Amount due",
            "Recurring events based on frequency (monthly, yearly, etc.)"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Managing Synced Bills</h2>
          <p className="text-muted-foreground mb-4">
            Control which bills appear in your calendar:
          </p>
          <Step number="1" title="Go to Bills Page">
            Navigate to Bills from the sidebar.
          </Step>
          <Step number="2" title="Toggle Calendar Sync">
            Use the toggle switch next to each bill to enable/disable calendar sync.
          </Step>
          <InfoBox>
            Calendar updates may take up to 24 hours to reflect changes, depending on your calendar app's refresh settings.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Troubleshooting</h2>
          <h3 className="font-semibold mt-4 mb-2">Calendar not updating?</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Most calendars refresh subscribed calendars every 24 hours</li>
            <li>Try removing and re-adding the calendar subscription</li>
            <li>Ensure the ICS URL is correct (regenerate if needed)</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-2">Bills not showing?</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Check that calendar sync is enabled for the bill</li>
            <li>Verify the bill is enabled (not disabled/hidden)</li>
            <li>Wait 24 hours for calendar to refresh</li>
          </ul>
        </section>
      </>
    )
  },

  "monthly-plan": {
    title: "Monthly Plan",
    description: "See your month-end financial snapshot",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Monthly Plan Overview</h2>
          <p className="text-muted-foreground mb-4">
            The Monthly Plan page gives you a clear picture of where you'll stand financially at the end of the month. 
            It combines your income, bills, and spending patterns to project your month-end balance.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Key Metrics</h2>
          <div className="space-y-4">
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-emerald-600">Projected Income</h3>
                <p className="text-sm text-muted-foreground">
                  Expected income this month based on your regular deposits (salary, recurring transfers).
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-red-600">Projected Expenses</h3>
                <p className="text-sm text-muted-foreground">
                  Total expected outflow including bills, subscriptions, and estimated discretionary spending.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-blue-600">Projected Savings</h3>
                <p className="text-sm text-muted-foreground">
                  What you'll have left after all expenses: Income minus Expenses.
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-purple-600">Savings Rate</h3>
                <p className="text-sm text-muted-foreground">
                  Percentage of income going to savings. Aim for at least 20%.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Upcoming Bills</h2>
          <p className="text-muted-foreground mb-4">
            See all bills due for the remainder of the month:
          </p>
          <FeatureList items={[
            "Bill name and merchant",
            "Due date and days until due",
            "Amount expected",
            "Running total of remaining bills"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Cash Flow Timeline</h2>
          <p className="text-muted-foreground mb-4">
            The visual timeline shows money moving in and out throughout the month:
          </p>
          <FeatureList items={[
            "Green bars — Income deposits",
            "Red bars — Bill payments and expenses",
            "Line graph — Your running balance over time"
          ]} />
          <Tip>
            Use the timeline to identify days when your balance might dip low. 
            Consider moving bill due dates if possible to smooth out cash flow.
          </Tip>
        </section>
      </>
    )
  },

  "insights": {
    title: "Insights & AI Predictions",
    description: "AI-powered spending analysis and forecasts",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">AI-Powered Insights</h2>
          <p className="text-muted-foreground mb-4">
            Tally uses GPT-4o to analyze your financial data and provide personalized, actionable insights. 
            Our AI understands Canadian financial context and gives advice tailored to your situation.
          </p>
          <Badge className="bg-emerald-100 text-emerald-700">New Feature</Badge>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">AI Summary Cards</h2>
          <p className="text-muted-foreground mb-4">
            Throughout Tally, you'll see AI Summary cards that provide context-specific insights:
          </p>
          <FeatureList items={[
            "Dashboard — Overall financial health assessment",
            "Transactions — Spending pattern analysis",
            "Budget — Budget adherence recommendations",
            "Bills — Bill payment optimization tips",
            "Insights — Deep spending analysis and predictions"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Spending Predictions</h2>
          <p className="text-muted-foreground mb-4">
            Our AI analyzes your last 3 months of spending to predict next month's expenses.
          </p>
          
          <h3 className="font-semibold mt-6 mb-2">What You'll See</h3>
          <FeatureList items={[
            "Predicted total spending for next month",
            "Comparison to last month (% change)",
            "Category-by-category predictions",
            "Trend indicators (increasing, decreasing, stable)",
            "Confidence levels (high, medium, low)"
          ]} />

          <h3 className="font-semibold mt-6 mb-2">How It Works</h3>
          <p className="text-muted-foreground mb-4">
            The prediction algorithm uses a weighted average that gives more importance to recent months:
          </p>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Last month: 50% weight</li>
            <li>Two months ago: 30% weight</li>
            <li>Three months ago: 20% weight</li>
          </ul>
          <p className="text-muted-foreground mt-2">
            Trends are then applied to adjust predictions up or down based on your spending trajectory.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Savings Opportunities</h2>
          <p className="text-muted-foreground mb-4">
            The AI identifies areas where you could potentially save money:
          </p>
          <FeatureList items={[
            "Categories with high spending relative to average",
            "Subscriptions you might not be using",
            "Dining out patterns that could be reduced",
            "Comparison to recommended spending percentages"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Warning Alerts</h2>
          <p className="text-muted-foreground mb-4">
            Get alerted to concerning trends before they become problems:
          </p>
          <FeatureList items={[
            "Categories with rapidly increasing spending",
            "Budget categories consistently over limit",
            "Unusual transaction patterns",
            "Projected shortfalls"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Spending Breakdown</h2>
          <p className="text-muted-foreground mb-4">
            Visual charts help you understand your spending:
          </p>
          <FeatureList items={[
            "Pie chart — Spending by category",
            "Bar chart — Month-over-month comparison",
            "Line chart — Daily spending patterns"
          ]} />
          <InfoBox>
            AI predictions require at least 1 month of transaction data. The more history available, 
            the more accurate predictions become.
          </InfoBox>
        </section>
      </>
    )
  },

  "goals": {
    title: "Financial Goals",
    description: "Set and track SMART financial goals",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Why Set Financial Goals?</h2>
          <p className="text-muted-foreground mb-4">
            Goals give your money purpose. Instead of wondering where your money went, 
            you'll know exactly what you're working toward.
          </p>
          <FeatureList items={[
            "Turn vague wishes into concrete targets",
            "Stay motivated with visible progress",
            "Make saving automatic and intentional",
            "Celebrate milestones along the way"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Creating a Goal</h2>
          <Step number="1" title="Navigate to Goals">
            Go to the Goals page from the sidebar.
          </Step>
          <Step number="2" title="Click New Goal">
            Click the "+ New Goal" button.
          </Step>
          <Step number="3" title="Enter Details">
            Provide:
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Goal name:</strong> e.g., "Emergency Fund", "Vacation 2027"</li>
              <li><strong>Target amount:</strong> How much you need to save</li>
              <li><strong>Target date:</strong> When you want to reach it</li>
              <li><strong>Starting amount:</strong> How much you've already saved (if any)</li>
            </ul>
          </Step>
          <Step number="4" title="Save">
            Click Create. Your goal is now active!
          </Step>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Goal Ideas</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: "Emergency Fund", desc: "3-6 months of expenses", amount: "$10,000 - $25,000" },
              { name: "Vacation", desc: "Dream trip fund", amount: "$2,000 - $10,000" },
              { name: "Down Payment", desc: "Home purchase savings", amount: "$25,000 - $100,000" },
              { name: "New Car", desc: "Vehicle replacement fund", amount: "$5,000 - $30,000" },
              { name: "Education", desc: "Course or degree", amount: "$1,000 - $50,000" },
              { name: "Wedding", desc: "Celebration fund", amount: "$10,000 - $50,000" }
            ].map((goal) => (
              <Card key={goal.name} className="border-border/50">
                <CardContent className="p-4">
                  <h3 className="font-semibold">{goal.name}</h3>
                  <p className="text-sm text-muted-foreground">{goal.desc}</p>
                  <p className="text-xs text-primary mt-1">{goal.amount}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Tracking Progress</h2>
          <p className="text-muted-foreground mb-4">Each goal displays:</p>
          <FeatureList items={[
            "Current amount vs. target",
            "Progress percentage with visual bar",
            "On-track indicator (based on time remaining)",
            "Monthly contribution needed to reach goal"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Updating Goals</h2>
          <p className="text-muted-foreground mb-4">
            Click on any goal to:
          </p>
          <FeatureList items={[
            "Add a contribution (record money saved)",
            "Update the current amount",
            "Adjust the target date",
            "Change the target amount",
            "Delete the goal"
          ]} />
          <Tip>
            Update your goals monthly when you move money into savings. This keeps your progress accurate and motivating.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Goal Strategies</h2>
          <Tip>
            <strong>Pay Yourself First:</strong> Transfer money to savings as soon as you get paid, 
            before spending on anything else.
          </Tip>
          <Tip>
            <strong>Automate:</strong> Set up automatic transfers to match your monthly contribution target.
          </Tip>
          <Tip>
            <strong>Visualize:</strong> Put a picture of your goal (vacation destination, dream car) 
            somewhere you'll see it daily.
          </Tip>
        </section>
      </>
    )
  },

  "net-worth": {
    title: "Net Worth Tracking",
    description: "Monitor your overall financial health",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">What is Net Worth?</h2>
          <p className="text-muted-foreground mb-4">
            Net worth is the single most important number in personal finance. It represents your total 
            financial position: everything you own minus everything you owe.
          </p>
          <div className="p-4 rounded-lg bg-muted/50 text-center my-6">
            <p className="text-lg font-semibold">Net Worth = Assets − Liabilities</p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Assets (What You Own)</h2>
          <p className="text-muted-foreground mb-4">
            Assets include all your valuable possessions and accounts:
          </p>
          <FeatureList items={[
            "Checking accounts",
            "Savings accounts",
            "Investment accounts (TFSA, RRSP)",
            "Property value (home, car)",
            "Valuable possessions",
            "Money owed to you"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Liabilities (What You Owe)</h2>
          <p className="text-muted-foreground mb-4">
            Liabilities are all your debts and obligations:
          </p>
          <FeatureList items={[
            "Credit card balances",
            "Student loans",
            "Car loans",
            "Mortgage balance",
            "Personal loans",
            "Other debts"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Automatic Tracking</h2>
          <p className="text-muted-foreground mb-4">
            Connected accounts are automatically included in your net worth:
          </p>
          <FeatureList items={[
            "Checking and savings accounts add to assets",
            "Credit card balances add to liabilities",
            "Balances update with each sync"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Adding Manual Assets/Liabilities</h2>
          <p className="text-muted-foreground mb-4">
            For items not connected through Plaid:
          </p>
          <Step number="1" title="Click Add Asset or Add Liability">
            Choose the appropriate button on the Net Worth page.
          </Step>
          <Step number="2" title="Enter Details">
            Provide a name (e.g., "Home Value", "Car Loan") and current amount.
          </Step>
          <Step number="3" title="Update Regularly">
            Update these values monthly to keep your net worth accurate.
          </Step>
          <Tip>
            For property values, use sites like HouseSigma or Realtor.ca to estimate. 
            Be conservative — it's better to underestimate.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Historical Tracking</h2>
          <p className="text-muted-foreground mb-4">
            Track your net worth over time to see your financial progress:
          </p>
          <FeatureList items={[
            "Monthly net worth chart",
            "Month-over-month change",
            "Year-over-year growth",
            "Identify trends and milestones"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Net Worth Targets by Age</h2>
          <p className="text-muted-foreground mb-4">
            General guidelines (adjust based on your situation):
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Age</th>
                  <th className="text-left py-2">Target Net Worth</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2">25</td>
                  <td className="py-2">0.5x annual salary</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">30</td>
                  <td className="py-2">1x annual salary</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">35</td>
                  <td className="py-2">2x annual salary</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">40</td>
                  <td className="py-2">3x annual salary</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">50</td>
                  <td className="py-2">6x annual salary</td>
                </tr>
                <tr>
                  <td className="py-2">60</td>
                  <td className="py-2">8x annual salary</td>
                </tr>
              </tbody>
            </table>
          </div>
          <InfoBox>
            These are guidelines, not rules. Focus on consistent growth rather than hitting exact targets.
          </InfoBox>
        </section>
      </>
    )
  },

  "cash-flow": {
    title: "Cash Flow Forecasting",
    description: "90-day projections and What-If scenarios",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Cash Flow Overview</h2>
          <p className="text-muted-foreground mb-4">
            Cash flow forecasting helps you see into the future. Know exactly when money will come in and go out, 
            and catch potential problems before they happen.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">90-Day Forecast</h2>
          <p className="text-muted-foreground mb-4">
            The forecast chart shows your projected balance for the next 90 days:
          </p>
          <FeatureList items={[
            "Starting balance from connected accounts",
            "Income events (salary, recurring deposits)",
            "Bill payments (detected and manual bills)",
            "Daily projected balance line",
            "Shortfall zones highlighted in red"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Shortfall Detection</h2>
          <p className="text-muted-foreground mb-4">
            Tally automatically identifies days when your balance might go negative:
          </p>
          <FeatureList items={[
            "Shortfall dates are highlighted on the chart",
            "Dashboard shows warning if shortfall detected",
            "Email alerts sent at noon EST if enabled",
            "Amount needed to cover shortfall is shown"
          ]} />
          <Warning>
            A projected shortfall means your bills might exceed your available funds on certain days. 
            Take action early by delaying bills, reducing spending, or adding income.
          </Warning>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">What-If Simulator</h2>
          <p className="text-muted-foreground mb-4">
            Model different scenarios to see how they impact your cash flow:
          </p>
          
          <h3 className="font-semibold mt-6 mb-2">Available Scenarios</h3>
          <FeatureList items={[
            "Delay a bill payment — See impact of pushing a bill to later",
            "Add one-time income — Model a bonus, tax refund, or gift",
            "Add one-time expense — See effect of a large purchase"
          ]} />

          <h3 className="font-semibold mt-6 mb-2">How to Use</h3>
          <Step number="1" title="Open the Simulator">
            Click "What-If Simulator" on the Cash Flow page.
          </Step>
          <Step number="2" title="Add a Scenario">
            Choose the type of scenario and enter details.
          </Step>
          <Step number="3" title="View Comparison">
            See your original forecast compared to the modified version.
          </Step>
          <Step number="4" title="Iterate">
            Add multiple scenarios to find the best approach.
          </Step>
          <Tip>
            Use What-If to plan large purchases. See exactly how buying that new TV affects your cash flow 
            for the next 3 months.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Cash Flow Alerts</h2>
          <p className="text-muted-foreground mb-4">
            Stay informed about potential problems:
          </p>
          <Step number="1" title="Enable Alerts">
            Go to Settings → Notifications → Enable Cash Flow Alerts.
          </Step>
          <Step number="2" title="Daily Check">
            Tally checks your forecast every day at noon EST.
          </Step>
          <Step number="3" title="Email Notification">
            If a shortfall is detected, you'll receive an email with:
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Date(s) of projected shortfall</li>
              <li>Amount needed to cover</li>
              <li>Upcoming bills contributing to shortfall</li>
            </ul>
          </Step>
        </section>
      </>
    )
  },

  "debt": {
    title: "Debt Payoff Planning",
    description: "Create a strategy to become debt-free",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Debt Freedom</h2>
          <p className="text-muted-foreground mb-4">
            Debt can feel overwhelming, but with a clear plan, you can systematically eliminate it. 
            Tally helps you create a debt payoff strategy that works for your situation.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Adding Your Debts</h2>
          <Step number="1" title="Navigate to Debt">
            Go to the Debt page from the sidebar.
          </Step>
          <Step number="2" title="Click Add Debt">
            Click the "+ Add Debt" button.
          </Step>
          <Step number="3" title="Enter Details">
            For each debt, provide:
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Name:</strong> e.g., "Visa Card", "Student Loan"</li>
              <li><strong>Current balance:</strong> Amount you owe today</li>
              <li><strong>Interest rate (APR):</strong> Annual percentage rate</li>
              <li><strong>Minimum payment:</strong> Required monthly payment</li>
            </ul>
          </Step>
          <Step number="4" title="Save">
            Click Add. Repeat for all your debts.
          </Step>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Payoff Strategies</h2>
          
          <h3 className="font-semibold mt-6 mb-3">Avalanche Method</h3>
          <p className="text-muted-foreground mb-4">
            Pay minimum on all debts, then put extra money toward the <strong>highest interest rate</strong> debt first.
          </p>
          <FeatureList items={[
            "Mathematically optimal — saves the most on interest",
            "Best for: People motivated by numbers",
            "May take longer to see debts eliminated"
          ]} />

          <h3 className="font-semibold mt-6 mb-3">Snowball Method</h3>
          <p className="text-muted-foreground mb-4">
            Pay minimum on all debts, then put extra money toward the <strong>smallest balance</strong> first.
          </p>
          <FeatureList items={[
            "Psychologically rewarding — quick wins keep you motivated",
            "Best for: People who need to see progress quickly",
            "May cost more in total interest"
          ]} />

          <InfoBox>
            Both methods work! The best strategy is the one you'll stick with. 
            Choose Avalanche if you're disciplined; choose Snowball if you need motivation.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Your Payoff Plan</h2>
          <p className="text-muted-foreground mb-4">
            Once you've added debts and chosen a strategy, Tally calculates:
          </p>
          <FeatureList items={[
            "Monthly payment schedule for each debt",
            "Payoff date for each debt",
            "Total interest you'll pay",
            "Your debt-free date"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Extra Payments</h2>
          <p className="text-muted-foreground mb-4">
            See the impact of paying more than the minimum:
          </p>
          <Step number="1" title="Enter Extra Amount">
            Input how much extra you can pay each month.
          </Step>
          <Step number="2" title="See the Difference">
            View your new debt-free date and interest savings.
          </Step>
          <Tip>
            Even an extra $50/month can shave years off your debt and save hundreds in interest!
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Debt Payoff Tips</h2>
          <Tip>
            <strong>Stop adding debt:</strong> Put credit cards in a drawer while paying them off. 
            Use cash or debit for purchases.
          </Tip>
          <Tip>
            <strong>Find extra money:</strong> Sell unused items, pick up a side gig, or cut 
            one subscription to accelerate payments.
          </Tip>
          <Tip>
            <strong>Automate payments:</strong> Set up automatic payments for at least the minimum 
            to avoid late fees and credit damage.
          </Tip>
          <Tip>
            <strong>Celebrate milestones:</strong> When you pay off a debt, celebrate! 
            Just don't celebrate by going into more debt.
          </Tip>
        </section>
      </>
    )
  },

  "courses": {
    title: "Financial Courses",
    description: "Learn personal finance with free educational content",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Learn Personal Finance</h2>
          <p className="text-muted-foreground mb-4">
            Knowledge is the foundation of financial success. Our free courses teach you essential 
            money skills in bite-sized lessons tailored for Canadians.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Available Courses</h2>
          <div className="space-y-4">
            {[
              { name: "Budgeting Basics", lessons: 5, duration: "30 min", desc: "Create a budget that actually works" },
              { name: "Building an Emergency Fund", lessons: 4, duration: "25 min", desc: "Why, how much, and where to keep it" },
              { name: "Understanding Credit", lessons: 6, duration: "40 min", desc: "Credit scores, reports, and improving credit" },
              { name: "Saving for Retirement", lessons: 5, duration: "35 min", desc: "RRSP, TFSA, and employer matching" },
              { name: "Debt Management", lessons: 5, duration: "35 min", desc: "Strategies to eliminate debt" }
            ].map((course) => (
              <Card key={course.name} className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{course.name}</h3>
                      <p className="text-sm text-muted-foreground">{course.desc}</p>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <div>{course.lessons} lessons</div>
                      <div>{course.duration}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Course Features</h2>
          <FeatureList items={[
            "Bite-sized lessons (5-10 minutes each)",
            "Canadian-specific content and examples",
            "Progress tracking across sessions",
            "No tests — learn at your own pace"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Earning Badges</h2>
          <p className="text-muted-foreground mb-4">
            Complete courses to earn badges and track your learning journey:
          </p>
          <div className="flex gap-8 my-6">
            <div className="text-center">
              <div className="text-4xl">🥉</div>
              <p className="font-medium">Bronze</p>
              <p className="text-sm text-muted-foreground">1 course</p>
            </div>
            <div className="text-center">
              <div className="text-4xl">🥈</div>
              <p className="font-medium">Silver</p>
              <p className="text-sm text-muted-foreground">3 courses</p>
            </div>
            <div className="text-center">
              <div className="text-4xl">🥇</div>
              <p className="font-medium">Gold</p>
              <p className="text-sm text-muted-foreground">All courses</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Tips for Learning</h2>
          <Tip>
            <strong>One lesson a day:</strong> Spend just 5-10 minutes daily. You'll complete a course in a week!
          </Tip>
          <Tip>
            <strong>Apply immediately:</strong> After each lesson, apply what you learned in Tally. 
            Create a budget, set a goal, or update your net worth.
          </Tip>
        </section>
      </>
    )
  },

  "household": {
    title: "Household Sharing",
    description: "Share finances with family members",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Family Finances Together</h2>
          <p className="text-muted-foreground mb-4">
            Money matters are better managed together. Household sharing lets you and your family members 
            see all accounts and transactions in one combined view.
          </p>
          <Badge className="bg-emerald-100 text-emerald-700">New Feature</Badge>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Creating a Household</h2>
          <Step number="1" title="Navigate to Settings">
            Go to Settings → Household.
          </Step>
          <Step number="2" title="Click Create Household">
            Click the "Create Household" button.
          </Step>
          <Step number="3" title="Name Your Household">
            Enter a name (e.g., "Smith Family", "Our Finances").
          </Step>
          <Step number="4" title="Share Your Code">
            You'll receive a 6-character invite code. Share this with family members.
          </Step>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Joining a Household</h2>
          <Step number="1" title="Get the Code">
            Ask the household owner for the 6-character invite code.
          </Step>
          <Step number="2" title="Navigate to Settings">
            Go to Settings → Household.
          </Step>
          <Step number="3" title="Click Join Household">
            Click the "Join Household" button.
          </Step>
          <Step number="4" title="Enter Code">
            Enter the invite code and click Join.
          </Step>
          <Tip>
            You can also be invited via email. The household owner can send you an email invite with the code.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">What's Shared</h2>
          <p className="text-muted-foreground mb-4">
            When you join a household, all members can see:
          </p>
          <FeatureList items={[
            "All connected bank accounts from all members",
            "Combined account balances",
            "All transactions from all accounts",
            "All bills and recurring payments",
            "Budget progress (combined spending)",
            "Financial goals"
          ]} />
          <InfoBox>
            All household members have equal access. There are no "viewer" or "limited" roles — everyone can see everything.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Household Management</h2>
          <h3 className="font-semibold mt-6 mb-2">For Owners</h3>
          <FeatureList items={[
            "Invite new members via email",
            "Remove members from the household",
            "Regenerate invite code (invalidates old code)",
            "Delete household (must remove all members first)"
          ]} />

          <h3 className="font-semibold mt-6 mb-2">For Members</h3>
          <FeatureList items={[
            "View and copy invite code to share",
            "Leave household at any time"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Multiple Bank Accounts</h2>
          <p className="text-muted-foreground mb-4">
            Each household member can connect their own bank accounts. All accounts appear together in the shared view:
          </p>
          <FeatureList items={[
            "Each member connects their own banks",
            "Combined cash balance shows total across all members",
            "Transactions appear together, sorted by date",
            "Budget tracks combined spending"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Leaving a Household</h2>
          <Step number="1" title="Navigate to Settings">
            Go to Settings → Household.
          </Step>
          <Step number="2" title="Click Leave Household">
            Click the "Leave Household" button at the bottom.
          </Step>
          <Step number="3" title="Confirm">
            Confirm your choice. Your accounts will no longer be visible to other members.
          </Step>
          <Warning>
            If you're the owner and there are other members, you must remove them first or transfer ownership before leaving.
          </Warning>
        </section>
      </>
    )
  },

  "settings": {
    title: "Settings & Account",
    description: "Manage your account, preferences, and connections",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Account Settings</h2>
          <p className="text-muted-foreground mb-4">
            Manage your personal information and account details:
          </p>
          <FeatureList items={[
            "Email — Your login email address",
            "Name — Your display name shown in the app",
            "Password — Change your password anytime"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Notification Settings</h2>
          <FeatureList items={[
            "Cash Flow Alerts — Email when shortfall detected",
            "Bill Reminders — Coming soon",
            "Weekly Summary — Coming soon"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Connected Accounts</h2>
          <p className="text-muted-foreground mb-4">
            Manage your bank connections:
          </p>
          <FeatureList items={[
            "View all linked accounts and last sync time",
            "Connect new banks",
            "Reconnect accounts that need re-authentication",
            "Disconnect accounts you no longer want to track"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Calendar Settings</h2>
          <p className="text-muted-foreground mb-4">
            Set up calendar integration for bill reminders:
          </p>
          <FeatureList items={[
            "Generate your unique calendar feed URL",
            "Regenerate URL if you want a new link",
            "View setup instructions for different calendar apps"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Subscription</h2>
          <p className="text-muted-foreground mb-4">
            Manage your Tally subscription:
          </p>
          <FeatureList items={[
            "View current plan and status",
            "See trial end date (if on trial)",
            "Upgrade to Tally Power",
            "Cancel subscription"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Household</h2>
          <p className="text-muted-foreground mb-4">
            Create or join a household to share finances:
          </p>
          <FeatureList items={[
            "Create a new household",
            "Join an existing household with invite code",
            "Manage household members (if owner)",
            "Leave household"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Data & Privacy</h2>
          <FeatureList items={[
            "Export Data — Download your data (coming soon)",
            "Delete Account — Permanently delete your account and all data"
          ]} />
          <Warning>
            Account deletion is permanent and cannot be undone. All your data, including transaction history, 
            will be permanently removed.
          </Warning>
        </section>
      </>
    )
  },

  "pricing": {
    title: "Subscription & Pricing",
    description: "Plans, pricing, and billing information",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Our Plans</h2>
          <p className="text-muted-foreground mb-4">
            Tally offers fair, age-based pricing. We believe financial tools should be accessible to everyone.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Plan Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3">Feature</th>
                  <th className="text-center py-3">Student<br/><span className="font-normal text-muted-foreground">Under 25</span></th>
                  <th className="text-center py-3">Power<br/><span className="font-normal text-muted-foreground">25-64</span></th>
                  <th className="text-center py-3">Senior<br/><span className="font-normal text-muted-foreground">65+</span></th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2">Price</td>
                  <td className="text-center py-2 text-emerald-600 font-semibold">Free</td>
                  <td className="text-center py-2 font-semibold">$14.99/mo</td>
                  <td className="text-center py-2 text-emerald-600 font-semibold">Free</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Bank connections</td>
                  <td className="text-center py-2">✓</td>
                  <td className="text-center py-2">✓</td>
                  <td className="text-center py-2">✓</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Sync frequency</td>
                  <td className="text-center py-2">Weekly</td>
                  <td className="text-center py-2 text-emerald-600">Daily</td>
                  <td className="text-center py-2">Weekly</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Transaction history</td>
                  <td className="text-center py-2">✓</td>
                  <td className="text-center py-2">✓</td>
                  <td className="text-center py-2">✓</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Bill tracking</td>
                  <td className="text-center py-2">✓</td>
                  <td className="text-center py-2">✓</td>
                  <td className="text-center py-2">✓</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Budgets</td>
                  <td className="text-center py-2">View only</td>
                  <td className="text-center py-2 text-emerald-600">Full access</td>
                  <td className="text-center py-2 text-emerald-600">Full access</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">AI insights</td>
                  <td className="text-center py-2">Basic</td>
                  <td className="text-center py-2 text-emerald-600">Advanced</td>
                  <td className="text-center py-2 text-emerald-600">Advanced</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Household sharing</td>
                  <td className="text-center py-2">—</td>
                  <td className="text-center py-2 text-emerald-600">✓</td>
                  <td className="text-center py-2">—</td>
                </tr>
                <tr>
                  <td className="py-2">Calendar sync</td>
                  <td className="text-center py-2">ICS only</td>
                  <td className="text-center py-2 text-emerald-600">All calendars</td>
                  <td className="text-center py-2 text-emerald-600">All calendars</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Yearly Savings</h2>
          <p className="text-muted-foreground mb-4">
            Save ~$30/year by choosing annual billing:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-muted-foreground text-sm">Monthly</p>
                <p className="text-2xl font-bold">$14.99/mo</p>
                <p className="text-sm text-muted-foreground">$179.88/year</p>
              </CardContent>
            </Card>
            <Card className="border-primary">
              <CardContent className="p-4 text-center">
                <Badge className="bg-emerald-100 text-emerald-700 mb-2">Save 17%</Badge>
                <p className="text-muted-foreground text-sm">Yearly</p>
                <p className="text-2xl font-bold">$149.99/yr</p>
                <p className="text-sm text-muted-foreground">$12.50/month equivalent</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">7-Day Free Trial</h2>
          <p className="text-muted-foreground mb-4">
            Power users get a full 7-day free trial:
          </p>
          <FeatureList items={[
            "Full access to all Power features",
            "Enter payment details to start (not charged during trial)",
            "Cancel anytime before trial ends — no charge",
            "Automatic billing starts after 7 days if you continue"
          ]} />
          <Tip>
            Set a calendar reminder for day 6 of your trial to decide whether to continue.
          </Tip>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Billing</h2>
          <FeatureList items={[
            "Charged in CAD (Canadian dollars)",
            "Secure payments via Stripe",
            "Receipts sent to your email",
            "Cancel anytime from Settings"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Cancellation</h2>
          <p className="text-muted-foreground mb-4">
            You can cancel your subscription anytime:
          </p>
          <Step number="1" title="Go to Settings">
            Navigate to Settings → Subscription.
          </Step>
          <Step number="2" title="Click Cancel">
            Click the Cancel Subscription button.
          </Step>
          <Step number="3" title="Keep Access">
            You'll retain access until the end of your current billing period.
          </Step>
          <InfoBox>
            We don't do refunds for partial months, but you keep full access until your paid period ends.
          </InfoBox>
        </section>
      </>
    )
  },

  "privacy": {
    title: "Privacy & Security",
    description: "How we protect your data",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Your Privacy Matters</h2>
          <p className="text-muted-foreground mb-4">
            We take your privacy seriously. Tally is built with security and privacy at its core.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Data Protection</h2>
          <FeatureList items={[
            "All data encrypted at rest and in transit (TLS 1.3)",
            "Bank-level security standards",
            "Regular security audits",
            "Data stored in Canadian data centers"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Bank Connection Security</h2>
          <p className="text-muted-foreground mb-4">
            Your bank connection is secured through Plaid:
          </p>
          <FeatureList items={[
            "Tally never sees your banking password",
            "Credentials are encrypted by Plaid",
            "Read-only access — we cannot move money",
            "Connection can be revoked anytime"
          ]} />
          <InfoBox>
            Plaid is trusted by thousands of financial apps and banks. They're SOC 2 Type II certified and undergo regular security audits.
          </InfoBox>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">What We Don't Do</h2>
          <FeatureList items={[
            "We never sell your data to third parties",
            "We never share your data with advertisers",
            "We never use your data for marketing purposes",
            "We never access your accounts without your permission"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">PIPEDA Compliance</h2>
          <p className="text-muted-foreground mb-4">
            Tally complies with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA):
          </p>
          <FeatureList items={[
            "Clear consent before collecting personal information",
            "Collection limited to what's necessary",
            "Information used only for stated purposes",
            "Reasonable security safeguards",
            "Right to access and correct your data",
            "Right to withdraw consent"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Your Rights</h2>
          <p className="text-muted-foreground mb-4">
            Under PIPEDA, you have the right to:
          </p>
          <FeatureList items={[
            "Access your personal data",
            "Request correction of inaccurate data",
            "Request deletion of your data",
            "Withdraw consent at any time",
            "File a complaint with the Privacy Commissioner"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Data Retention</h2>
          <FeatureList items={[
            "Transaction history retained while account is active",
            "Data deleted within 30 days of account deletion",
            "Backups encrypted and secured",
            "No data retained after you leave"
          ]} />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Contact Us</h2>
          <p className="text-muted-foreground">
            For privacy concerns or data requests:
          </p>
          <p className="font-medium mt-2">privacy@usetally.ca</p>
        </section>
      </>
    )
  },

  "faq": {
    title: "FAQ & Troubleshooting",
    description: "Common questions and solutions",
    content: () => (
      <>
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold">Is Tally free?</h3>
              <p className="text-muted-foreground mt-1">
                Free for users under 25 (Student) or 65+ (Senior). Users 25-64 pay $14.99/month 
                for Tally Power, with a 7-day free trial.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">Is my bank supported?</h3>
              <p className="text-muted-foreground mt-1">
                We support 50+ Canadian banks including TD, RBC, Scotiabank, BMO, CIBC, and many more. 
                Try searching for your bank during the connection process.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">Can Tally access my money?</h3>
              <p className="text-muted-foreground mt-1">
                No. Tally has read-only access through Plaid. We can view transactions and balances, 
                but we cannot move money, make payments, or modify your accounts in any way.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">How often does data sync?</h3>
              <p className="text-muted-foreground mt-1">
                Power users: Daily automatic sync + manual sync anytime.<br/>
                Student/Senior: Weekly automatic sync (Wednesdays) + manual sync once per week.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">Can I use Tally on my phone?</h3>
              <p className="text-muted-foreground mt-1">
                Yes! Tally is a responsive web app that works great on mobile browsers. 
                Add it to your home screen for app-like experience. Native apps coming soon.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">How do I cancel my subscription?</h3>
              <p className="text-muted-foreground mt-1">
                Go to Settings → Subscription → Cancel. You'll keep access until the end of 
                your current billing period.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">Can I export my data?</h3>
              <p className="text-muted-foreground mt-1">
                Data export is coming soon. For now, contact support@usetally.ca if you need your data.
              </p>
            </div>

            <div>
              <h3 className="font-semibold">Is my data safe?</h3>
              <p className="text-muted-foreground mt-1">
                Yes. All data is encrypted, we're PIPEDA compliant, and we never sell your data. 
                See our Privacy & Security section for details.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Troubleshooting</h2>
          
          <h3 className="font-semibold mt-6 mb-2">Bank won't connect</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Verify your online banking credentials are correct</li>
            <li>Check if your bank requires enabling third-party access</li>
            <li>Try using website credentials, not app credentials</li>
            <li>Some banks have connection delays — try again in a few hours</li>
            <li>Contact support if the issue persists</li>
          </ul>

          <h3 className="font-semibold mt-6 mb-2">Transactions not showing</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Click "Sync Now" in Settings to refresh</li>
            <li>Wait a few minutes for sync to complete</li>
            <li>Some transactions take 1-2 days to appear</li>
            <li>Check if the account is still connected</li>
          </ul>

          <h3 className="font-semibold mt-6 mb-2">Categories are wrong</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Click the transaction to change its category</li>
            <li>Changes are saved automatically</li>
            <li>Re-categorizing helps Tally learn your preferences</li>
          </ul>

          <h3 className="font-semibold mt-6 mb-2">Calendar not syncing</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Calendar apps refresh subscriptions every 24 hours</li>
            <li>Try removing and re-adding the calendar subscription</li>
            <li>Regenerate the feed URL if needed</li>
          </ul>

          <h3 className="font-semibold mt-6 mb-2">App is slow or not loading</h3>
          <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
            <li>Try refreshing the page (Ctrl+R or Cmd+R)</li>
            <li>Clear browser cache and cookies</li>
            <li>Try a different browser</li>
            <li>Check your internet connection</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-4">Contact Support</h2>
          <p className="text-muted-foreground mb-4">
            Still need help? We're here for you:
          </p>
          <Card className="border-border/50">
            <CardContent className="p-6 text-center">
              <p className="font-semibold text-lg">support@usetally.ca</p>
              <p className="text-muted-foreground mt-2">Response time: Within 24-48 hours</p>
            </CardContent>
          </Card>
          <Tip>
            Include your account email and a detailed description of the issue for fastest resolution.
          </Tip>
        </section>
      </>
    )
  }
};

// Main DocsDetailPage component
export const DocsDetailPage = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentSection = docContent[sectionId];
  const currentIndex = docSections.findIndex(s => s.id === sectionId);
  const prevSection = currentIndex > 0 ? docSections[currentIndex - 1] : null;
  const nextSection = currentIndex < docSections.length - 1 ? docSections[currentIndex + 1] : null;

  useEffect(() => {
    if (currentSection) {
      document.title = `${currentSection.title} | Tally Docs`;
    }
    window.scrollTo(0, 0);
  }, [sectionId, currentSection]);

  if (!currentSection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Section not found</h1>
          <Button onClick={() => navigate("/docs")}>Back to Documentation</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-muted rounded"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <Link to="/docs" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">All Topics</span>
              </Link>
            </div>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">T</span>
              </div>
              <span className="font-heading font-bold text-lg hidden sm:inline">Tally Docs</span>
            </Link>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-[65px] left-0 h-[calc(100vh-65px)] w-64 bg-background border-r border-border/50 overflow-y-auto z-40
          transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-1">
            {docSections.map((section) => {
              const Icon = section.icon;
              const isActive = section.id === sectionId;
              return (
                <Link
                  key={section.id}
                  to={`/docs/${section.id}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors
                    ${isActive 
                      ? 'bg-primary/10 text-primary font-medium' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{section.title}</span>
                  {section.isNew && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs ml-auto">New</Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-3xl mx-auto px-4 py-8 lg:py-12">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link to="/docs" className="hover:text-foreground">Documentation</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground">{currentSection.title}</span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl lg:text-4xl font-bold mb-3">{currentSection.title}</h1>
            <p className="text-lg text-muted-foreground mb-8">{currentSection.description}</p>

            {/* Content */}
            <div className="prose prose-slate dark:prose-invert max-w-none">
              {currentSection.content()}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-12 pt-8 border-t border-border/50">
              {prevSection ? (
                <Link 
                  to={`/docs/${prevSection.id}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>{prevSection.title}</span>
                </Link>
              ) : <div />}
              {nextSection ? (
                <Link 
                  to={`/docs/${nextSection.id}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>{nextSection.title}</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              ) : <div />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DocsDetailPage;
